import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  MetricsState,
  HistoricalMetric,
  RevenueSource,
  AdUnit,
  DashboardSummary,
  DateRange,
} from "../../types";
import metricsService from "../../services/metricsService";

const initialState: MetricsState = {
  summary: null,
  dailyMetrics: [],
  revenueBySource: [],
  adUnits: [],
  historicalData: [],
  realTimeMetrics: null,
  isLoading: false,
  period: "7d",
  dateRange: {
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  },
};

// Helper function to safely convert to number
const safeNumber = (value: any, defaultValue: number = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  const num = parseFloat(value);
  return isNaN(num) ? defaultValue : num;
};

export const fetchDashboardMetrics = createAsyncThunk(
  "metrics/fetchDashboard",
  async (
    params: { period?: string; startDate?: string; endDate?: string } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await metricsService.getDashboardMetrics(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch dashboard metrics"
      );
    }
  }
);

export const fetchHistoricalData = createAsyncThunk(
  "metrics/fetchHistorical",
  async (
    params: { startDate?: string; endDate?: string } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await metricsService.getHistoricalData(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch historical data"
      );
    }
  }
);

export const fetchRealTimeMetrics = createAsyncThunk(
  "metrics/fetchRealTime",
  async (_, { rejectWithValue }) => {
    try {
      const response = await metricsService.getRealTimeMetrics();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch real-time metrics"
      );
    }
  }
);

const metricsSlice = createSlice({
  name: "metrics",
  initialState,
  reducers: {
    setPeriod: (
      state,
      action: PayloadAction<"7d" | "30d" | "90d" | "custom">
    ) => {
      state.period = action.payload;
    },
    setDateRange: (state, action: PayloadAction<DateRange>) => {
      state.dateRange = action.payload;
      state.period = "custom";
    },
    clearMetrics: (state) => {
      state.summary = null;
      state.dailyMetrics = [];
      state.revenueBySource = [];
    },
    updateBalance: (state, action: PayloadAction<number>) => {
      if (state.summary) {
        state.summary.balance = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard Metrics
      .addCase(fetchDashboardMetrics.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchDashboardMetrics.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;

          const data = action.payload?.data || action.payload;

          // Handle summary data
          if (data?.summary) {
            state.summary = {
              total_revenue: safeNumber(data.summary.total_revenue),
              total_page_views: safeNumber(data.summary.total_page_views),
              total_clicks: safeNumber(data.summary.total_clicks),
              total_impressions: safeNumber(data.summary.total_impressions),
              avg_cpc: safeNumber(data.summary.avg_cpc),
              avg_cpm: safeNumber(data.summary.avg_cpm),
              avg_ctr: safeNumber(data.summary.avg_ctr),
              balance: safeNumber(data.summary.balance || 0),
              currency: data.summary.currency || "USD",
            };
          } else {
            state.summary = {
              total_revenue: 0,
              total_page_views: 0,
              total_clicks: 0,
              total_impressions: 0,
              avg_cpc: 0,
              avg_cpm: 0,
              avg_ctr: 0,
              balance: 0,
              currency: "USD",
            };
          }

          // Handle daily metrics
          if (Array.isArray(data?.daily_metrics)) {
            state.dailyMetrics = data.daily_metrics.map((metric: any) => ({
              ...metric,
              revenue: safeNumber(metric.revenue),
              page_views: safeNumber(metric.page_views),
              clicks: safeNumber(metric.clicks),
              impressions: safeNumber(metric.impressions),
              cpc: safeNumber(metric.cpc),
              cpm: safeNumber(metric.cpm),
              ctr: safeNumber(metric.ctr),
              date: metric.date || "",
            }));
          } else {
            state.dailyMetrics = [];
          }

          // Handle revenue by source
          if (Array.isArray(data?.revenue_by_source)) {
            state.revenueBySource = data.revenue_by_source.map(
              (source: any) => ({
                ...source,
                amount: safeNumber(source.amount || source.revenue),
                source_type: source.source_type || "unknown",
              })
            );
          } else {
            state.revenueBySource = [];
          }

          // Handle ad units
          state.adUnits = Array.isArray(data?.ad_units) ? data.ad_units : [];
        }
      )
      .addCase(fetchDashboardMetrics.rejected, (state) => {
        state.isLoading = false;
        state.summary = {
          total_revenue: 0,
          total_page_views: 0,
          total_clicks: 0,
          total_impressions: 0,
          avg_cpc: 0,
          avg_cpm: 0,
          avg_ctr: 0,
          balance: 0,
          currency: "USD",
        };
        state.dailyMetrics = [];
        state.revenueBySource = [];
        state.adUnits = [];
      });
  },
});

export const { setPeriod, setDateRange, clearMetrics, updateBalance } =
  metricsSlice.actions;
export default metricsSlice.reducer;
